/* eslint-disable @atlaskit/design-system/no-html-button */
import React from 'react';
import { styled } from '@compiled/react';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowDownIconOld from '@atlaskit/icon/glyph/arrow-down';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useMoreResultsStore } from '../../../services/more-results';
import { useBacklogSelector } from '../../../state';
import { getFilters } from '../../../state/selectors/filters';
import messages from './messages';

type Props = {
	scrollableParentRef: React.RefObject<HTMLDivElement | null>;
};

export const MoreResults = ({ scrollableParentRef }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const filters = useBacklogSelector(getFilters);
	const [{ isLastResultMarkerBelowFold }] = useMoreResultsStore();

	const filtersAreApplied = Object.keys(filters).length > 0;
	const { formatMessage } = useIntl();

	const handleClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'moreResultsButton',
		});

		fireUIAnalytics(analyticsEvent);

		if (scrollableParentRef.current) {
			scrollableParentRef.current.scrollTo({
				top: scrollableParentRef.current.scrollTop + scrollableParentRef.current.clientHeight,
				behavior: 'smooth',
			});
		}
	};

	if (!filtersAreApplied || !isLastResultMarkerBelowFold) return null;

	return (
		<Container>
			<SeeMoreButton onClick={handleClick} tabIndex={-1} aria-hidden>
				{fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
					<ArrowDownIcon
						label={formatMessage(messages.downArrowIconLabel)}
						spacing="spacious"
						LEGACY_fallbackIcon={ArrowDownIconOld}
						color={token('color.icon')}
					/>
				) : (
					<ArrowDownIconOld label={formatMessage(messages.downArrowIconLabel)} />
				)}
				{formatMessage(messages.moreResults)}
			</SeeMoreButton>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.card,
	left: 0,
	right: 0,
	bottom: token('space.300', '24px'),
	display: 'flex',
	justifyContent: 'center',
	pointerEvents: 'none',
	height: 0, // don't take up vertical space, so there isn't a jump when it is removed from DOM
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SeeMoreButton = styled.button({
	display: 'flex',
	alignItems: 'center',
	width: 'fit-content',
	height: 'fit-content',
	padding: `${token('space.050', '4px')} ${token('space.150', '12px')}`,
	border: 'none',
	borderRadius: 16,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.overlay', colors.N0),
	boxShadow: token(
		'elevation.shadow.overlay',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A}`,
	),
	cursor: 'pointer',
	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.accent.blue', colors.B400),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.selected.hovered', colors.B200),
	},
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		background: token('color.background.accent.gray.subtler', colors.N30),
	},
	pointerEvents: 'auto',
});
